import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { saludoDeLaHora } from '@/lib/utils/saludo-de-la-hora';
import {
  FileText,
  DollarSign,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  ArrowRight,
  BarChart3,
  AlertCircle,
  XCircle,
  Users,
  Target,
  Activity,
  CalendarClock,
  Percent,
  Layers,
  Filter,
  Lightbulb,
  Tag,
  Sparkles,
} from 'lucide-react';
import { FadeIn, StaggerChildren, StaggerItem, AnimatedCounter } from '@/components/motion';
import { formatCurrency, formatRelativeTime } from '@/lib/utils/format';
import { montoCorto } from '@/features/inicio/lib/resumen-de-inicio';
import { formatoNumero, formatoPorcentaje } from '@/lib/utils/formato-precio';
import { dashboardService, type DashboardStats } from '../services/dashboard-service';
import { ExchangeRateWidget } from '../components/exchange-rate-widget';
import { MarketAlertsWidget } from '../components/market-alerts-widget';
import { destinoNuevaCotizacion } from '@/features/quotes/utils/destino-nueva-cotizacion';
import {
  ComposedChart,
  Area,
  Bar,
  BarChart,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info';
    color: string;
  }
> = {
  DRAFT: { label: 'Borrador', variant: 'secondary', color: 'bg-muted' },
  PENDING_APPROVAL: { label: 'Pendiente', variant: 'warning', color: 'bg-alerta' },
  APPROVED: { label: 'Aprobada', variant: 'success', color: 'bg-ok' },
  REJECTED: { label: 'Rechazada', variant: 'destructive', color: 'bg-bloqueo' },
  EXPIRED: { label: 'Expirada', variant: 'outline', color: 'bg-muted' },
  ADJUSTMENT_REQUESTED: { label: 'Ajuste', variant: 'info', color: 'bg-info' },
  CANCELLED: { label: 'Cancelada', variant: 'outline', color: 'bg-muted' },
};

function MonthDelta({
  current,
  previous,
  inverted = false,
}: {
  current: number;
  previous: number;
  inverted?: boolean;
}) {
  if (!previous && !current) return null;
  const diff = current - previous;
  if (previous === 0) {
    return (
      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
        <TrendingUp className="h-3 w-3" /> nuevo este mes
      </p>
    );
  }
  const pct = (diff / Math.abs(previous)) * 100;
  const isUp = diff > 0;
  const positive = inverted ? !isUp : isUp;
  const Icon = diff === 0 ? Minus : isUp ? TrendingUp : TrendingDown;
  const colorClass = diff === 0 ? 'text-muted-foreground' : positive ? 'text-ok' : 'text-bloqueo';
  return (
    <p
      className={`text-xs mt-0.5 flex items-center gap-1 font-medium ${colorClass}`}
      title="vs mes anterior"
    >
      <Icon className="h-3 w-3" />
      {pct > 0 ? '+' : ''}
      {pct.toFixed(0)}% vs mes pasado
    </p>
  );
}

type InsightSeverity = 'good' | 'warning' | 'info';
interface Insight {
  icon: typeof Lightbulb;
  text: string;
  severity: InsightSeverity;
  onClick?: () => void;
}

function computeInsights(stats: DashboardStats, navigate: (path: string) => void): Insight[] {
  const insights: Insight[] = [];

  // 1. Cotizaciones por vencer urgentes (<=2d)
  const urgent = stats.expiringSoonQuotes.filter((q) => q.daysLeft <= 2);
  if (urgent.length > 0) {
    insights.push({
      icon: CalendarClock,
      text: `${urgent.length} cotización${urgent.length > 1 ? 'es' : ''} vence${urgent.length > 1 ? 'n' : ''} en ≤2 días`,
      severity: 'warning',
      onClick: () => navigate('/quotes?status=PENDING_APPROVAL'),
    });
  } else if (stats.expiringSoonQuotes.length > 0) {
    insights.push({
      icon: CalendarClock,
      text: `${stats.expiringSoonQuotes.length} por vencer en 7 días`,
      severity: 'info',
    });
  }

  // 2. Tendencia de ingresos vs mes anterior
  const { current, previous } = stats.comparison;
  if (previous.revenue > 0) {
    const revPct = ((current.revenue - previous.revenue) / previous.revenue) * 100;
    if (revPct >= 10) {
      insights.push({
        icon: TrendingUp,
        text: `Ingresos +${revPct.toFixed(0)}% vs mes pasado`,
        severity: 'good',
      });
    } else if (revPct <= -10) {
      insights.push({
        icon: TrendingDown,
        text: `Ingresos ${revPct.toFixed(0)}% vs mes pasado`,
        severity: 'warning',
      });
    }
  }

  // 3. Concentracion en top cliente
  if (stats.topClients.length > 0 && stats.revenue.totalApproved > 0) {
    const top = stats.topClients[0];
    const concentration = (top.revenue / stats.revenue.totalApproved) * 100;
    if (concentration >= 35) {
      insights.push({
        icon: Users,
        text: `${top.name.length > 18 ? top.name.slice(0, 16) + '…' : top.name} concentra el ${concentration.toFixed(0)}% del pipeline aprobado`,
        severity: 'warning',
      });
    }
  }

  // 4. Margen
  if (stats.marginMetrics.sampleSize > 0) {
    if (stats.marginMetrics.avgMargin >= 25) {
      insights.push({
        icon: Sparkles,
        text: `Margen promedio saludable: ${stats.marginMetrics.avgMargin.toFixed(1)}%`,
        severity: 'good',
      });
    } else if (stats.marginMetrics.avgMargin < 10) {
      insights.push({
        icon: AlertCircle,
        text: `Margen promedio bajo: ${stats.marginMetrics.avgMargin.toFixed(1)}%`,
        severity: 'warning',
      });
    }
  }

  // 5. Descuento
  if (stats.discountMetrics.approvedCount > 0 && stats.discountMetrics.discountedPct >= 50) {
    insights.push({
      icon: Tag,
      text: `${stats.discountMetrics.discountedPct.toFixed(0)}% de tus aprobadas tienen descuento (avg ${stats.discountMetrics.avgDiscountPct.toFixed(0)}%)`,
      severity: 'info',
    });
  }

  // 6. Forecast
  if (stats.forecast.forecastedRevenue > 0 && stats.forecast.totalPending > 0) {
    insights.push({
      icon: Target,
      text: `Proyección del pipeline: ${montoCorto(stats.forecast.forecastedRevenue)}`,
      severity: 'info',
    });
  }

  return insights.slice(0, 4);
}

function formatMonthLabel(month: string): string {
  const [, m] = month.split('-');
  const months = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];
  return months[parseInt(m, 10) - 1] || m;
}

export function DashboardPage() {
  const { fullName, user, isAdmin, canSeeMenu, isModuleLocked } = useAuth();
  const navigate = useNavigate();

  /*
    🔴 El Dashboard ofrecía «Nueva Cotizacion» a quien no tiene ese módulo.

    Reportado desde producción el 2026-09-10: con el rol «Negocios» —que
    trabaja con Cotizaciones Datec y no con cotizaciones normales—
    `menu:nueva-cotizacion` está en Oculto, así que el botón llevaba derecho a
    «Módulo no disponible». Y ese cartel dice «contactá al administrador de tu
    empresa», o sea que además manda a pedir un permiso que no hace falta para
    trabajar.

    29-sep: además, quien tiene Cotizaciones Datec va ahí y no al cotizador
    general, y un cliente sin el Pricer Datec va al general. La regla vive en
    `destinoNuevaCotizacion`, compartida con el listado de Cotizaciones.
  */
  const nuevaCotizacion = destinoNuevaCotizacion(canSeeMenu);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dashboardService
      .getStats()
      .then(setStats)
      .catch((err) => setError(err?.message || 'No se pudieron cargar los números'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/* El encabezado de Cotiza (artefacto «Cotiza · Visión general», 19-oct). */}
        <PageHeader
          modulo="cotiza"
          etiqueta="Cotiza"
          icono={FileText}
          titulo={
            <>
              {saludoDeLaHora()},{' '}
              <span className="bg-degradado-prixia-texto bg-clip-text text-transparent">
                {fullName?.split(' ')[0]}.
              </span>
            </>
          }
          subtitulo="Resumen de tu actividad de pricing: lo que necesita tu atención hoy."
          acciones={
            nuevaCotizacion && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-tour="dashboard-new-quote"
                    variant="degradado"
                    size="lg"
                    className="px-5"
                    onClick={() => navigate(nuevaCotizacion.href)}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {nuevaCotizacion.label}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Crear una nueva cotización</TooltipContent>
              </Tooltip>
            )
          }
        />

        {error && (
          <FadeIn>
            <Card className="border-destructive/50 bg-destructive/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="w-4 h-4" />
                  <p className="text-sm">{error}</p>
                </div>
              </CardContent>
            </Card>
          </FadeIn>
        )}

        {/* Insights Banner */}
        {!loading &&
          stats &&
          (() => {
            const insights = computeInsights(stats, navigate);
            if (insights.length === 0) return null;
            return (
              <FadeIn delay={0.08}>
                <section
                  aria-label="Lo que necesita tu atención"
                  className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
                >
                  {insights.map((ins, i) => {
                    const tono =
                      ins.severity === 'good'
                        ? 'bg-ok-suave text-ok'
                        : ins.severity === 'warning'
                          ? 'bg-alerta-suave text-alerta'
                          : 'bg-info-suave text-info';
                    const Icon = ins.icon;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={ins.onClick}
                        disabled={!ins.onClick}
                        className="group flex items-center gap-3 rounded-tarjeta border bg-card p-4 text-left shadow-sm transition-[border-color,box-shadow] duration-snap ease-snap enabled:hover:shadow-md disabled:cursor-default"
                      >
                        <span
                          className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${tono}`}
                        >
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1 text-sm font-semibold">{ins.text}</span>
                        {ins.onClick && (
                          <ArrowRight
                            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </section>
              </FadeIn>
            );
          })()}

        {/* KPI Cards */}
        <div data-tour="dashboard-kpis" className="space-y-3">
          {/* Como el artefacto: el rótulo de la sección con su raya. */}
          <p className="flex items-center gap-3 text-xs font-semibold tracking-[0.14em] text-muted-foreground">
            ESTE MES
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
          </p>
          <StaggerChildren
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4"
            staggerDelay={0.08}
            initialDelay={0.1}
          >
            <StaggerItem>
              <Card className="relative overflow-hidden">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Total cotizaciones
                      </p>
                      <div className="font-titulo text-[32px] font-semibold leading-tight mt-1">
                        {loading ? (
                          <Skeleton className="h-9 w-16" />
                        ) : (
                          <AnimatedCounter target={stats?.quotes.total ?? 0} />
                        )}
                      </div>
                      {!loading && stats && stats.quotes.draft > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {stats.quotes.draft} en borrador
                        </p>
                      )}
                      {/* Las canceladas van APARTE: no son activas ni perdidas. */}
                      {!loading && stats && (stats.quotes.cancelled ?? 0) > 0 && (
                        <p
                          className="text-xs text-muted-foreground mt-0.5"
                          data-testid="canceladas-aparte"
                        >
                          {stats.quotes.cancelled}{' '}
                          {stats.quotes.cancelled === 1 ? 'cancelada' : 'canceladas'} (aparte)
                        </p>
                      )}
                      {!loading && stats && (
                        <MonthDelta
                          current={stats.comparison.current.total}
                          previous={stats.comparison.previous.total}
                        />
                      )}
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-info/10 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-info" />
                    </div>
                  </div>
                </CardContent>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-info/20">
                  <div
                    className="h-full bg-info transition-all duration-1000"
                    style={{ width: loading ? '0%' : '100%' }}
                  />
                </div>
              </Card>
            </StaggerItem>

            <StaggerItem>
              <Card className="relative overflow-hidden">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Aprobadas</p>
                      <div className="font-titulo text-[32px] font-semibold leading-tight mt-1 text-ok">
                        {loading ? (
                          <Skeleton className="h-9 w-16" />
                        ) : (
                          <AnimatedCounter target={stats?.quotes.approved ?? 0} />
                        )}
                      </div>
                      {!loading && stats && (
                        <p className="text-xs font-semibold text-ok mt-1">
                          {formatCurrency(stats.revenue.totalApproved)}
                        </p>
                      )}
                      {!loading && stats && stats.quotes.total > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatoPorcentaje((stats.quotes.approved / stats.quotes.total) * 100, {
                            decimales: 0,
                          })}{' '}
                          de aprobación
                        </p>
                      )}
                      {!loading && stats && (
                        <MonthDelta
                          current={stats.comparison.current.approved}
                          previous={stats.comparison.previous.approved}
                        />
                      )}
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-ok/10 flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-ok" />
                    </div>
                  </div>
                </CardContent>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-ok/20">
                  <div
                    className="h-full bg-ok transition-all duration-1000"
                    style={{
                      width:
                        loading || !stats || stats.quotes.total === 0
                          ? '0%'
                          : `${(stats.quotes.approved / stats.quotes.total) * 100}%`,
                    }}
                  />
                </div>
              </Card>
            </StaggerItem>

            <StaggerItem>
              <Card className="relative overflow-hidden">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Pendientes</p>
                      <div className="font-titulo text-[32px] font-semibold leading-tight mt-1 text-alerta">
                        {loading ? (
                          <Skeleton className="h-9 w-16" />
                        ) : (
                          <AnimatedCounter target={stats?.quotes.pendingApproval ?? 0} />
                        )}
                      </div>
                      {!loading && stats && (
                        <p className="text-xs font-semibold text-alerta mt-1">
                          {formatCurrency(stats.revenue.totalPending)}
                        </p>
                      )}
                      {!loading && stats && stats.approvals.avgResolutionHours > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          ~{formatoNumero(stats.approvals.avgResolutionHours, 1)} h en promedio
                        </p>
                      )}
                      {!loading && stats && (
                        <MonthDelta
                          current={stats.comparison.current.pending}
                          previous={stats.comparison.previous.pending}
                        />
                      )}
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-alerta/10 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-alerta" />
                    </div>
                  </div>
                </CardContent>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-alerta/20">
                  <div
                    className="h-full bg-alerta transition-all duration-1000"
                    style={{
                      width:
                        loading || !stats || stats.quotes.total === 0
                          ? '0%'
                          : `${(stats.quotes.pendingApproval / stats.quotes.total) * 100}%`,
                    }}
                  />
                </div>
              </Card>
            </StaggerItem>

            <StaggerItem>
              <Card className="relative overflow-hidden">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Perdidas</p>
                      <div className="font-titulo text-[32px] font-semibold leading-tight mt-1 text-bloqueo">
                        {loading ? (
                          <Skeleton className="h-9 w-16" />
                        ) : (
                          <AnimatedCounter target={stats?.revenue.lostCount ?? 0} />
                        )}
                      </div>
                      {!loading && stats && (
                        <p className="text-xs font-semibold text-bloqueo mt-1">
                          {formatCurrency(stats.revenue.totalLost)}
                        </p>
                      )}
                      {!loading && stats && stats.quotes.total > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {stats.quotes.rejected} rechazadas + {stats.quotes.expired} expiradas
                        </p>
                      )}
                      {!loading && stats && (
                        <MonthDelta
                          current={stats.comparison.current.lost}
                          previous={stats.comparison.previous.lost}
                          inverted
                        />
                      )}
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-bloqueo/10 flex items-center justify-center">
                      <XCircle className="h-6 w-6 text-bloqueo" />
                    </div>
                  </div>
                </CardContent>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-bloqueo/20">
                  <div
                    className="h-full bg-bloqueo transition-all duration-1000"
                    style={{
                      width:
                        loading || !stats || stats.quotes.total === 0
                          ? '0%'
                          : `${(stats.revenue.lostCount / stats.quotes.total) * 100}%`,
                    }}
                  />
                </div>
              </Card>
            </StaggerItem>

            <StaggerItem>
              <Card className="relative overflow-hidden">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Ingresos aprobados
                      </p>
                      {/* Corto («USD 2,31 M»): «$2308579» no entraba en la tarjeta y tapaba el ícono. */}
                      <div
                        className="font-titulo text-[32px] font-semibold leading-tight mt-1 whitespace-nowrap"
                        title={stats ? formatCurrency(stats.revenue.totalApproved ?? 0) : undefined}
                      >
                        {loading ? (
                          <Skeleton className="h-9 w-24" />
                        ) : (
                          montoCorto(stats?.revenue.totalApproved ?? 0)
                        )}
                      </div>
                      {!loading && stats && stats.revenue.avgDealSize > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Promedio: {formatCurrency(stats.revenue.avgDealSize)}
                        </p>
                      )}
                      {!loading && stats && (
                        <MonthDelta
                          current={stats.comparison.current.revenue}
                          previous={stats.comparison.previous.revenue}
                        />
                      )}
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-violet-500" />
                    </div>
                  </div>
                </CardContent>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-violet-500/20">
                  <div
                    className="h-full bg-violet-500 transition-all duration-1000"
                    style={{ width: loading ? '0%' : '100%' }}
                  />
                </div>
              </Card>
            </StaggerItem>
          </StaggerChildren>
        </div>

        {/* Middle Row: Chart + Status Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Trend */}
          <FadeIn delay={0.3} className="lg:col-span-2">
            <Card data-tour="dashboard-revenue" className="h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-muted-foreground" />
                      Tendencia de cotizaciones
                    </CardTitle>
                    <CardDescription>Últimos 6 meses</CardDescription>
                  </div>
                  {!loading && stats && (
                    <Badge variant="info">
                      {stats.quotesByMonth.reduce((s, m) => s + m.count, 0)} cotizaciones
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-end gap-3 h-56">
                    {[60, 80, 45, 90, 70, 100].map((h, i) => (
                      <Skeleton key={i} className="flex-1 rounded-t" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                ) : stats && stats.quotesByMonth.length > 0 ? (
                  <div className="space-y-3">
                    <div className="h-56 -ml-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={stats.quotesByMonth.map((m) => ({
                            ...m,
                            label: formatMonthLabel(m.month),
                          }))}
                          margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop
                                offset="0%"
                                stopColor="hsl(var(--primary))"
                                stopOpacity={0.35}
                              />
                              <stop
                                offset="100%"
                                stopColor="hsl(var(--primary))"
                                stopOpacity={0.02}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="hsl(var(--muted-foreground) / 0.15)"
                          />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                            tickLine={false}
                            axisLine={{ stroke: 'hsl(var(--muted-foreground) / 0.2)' }}
                          />
                          <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                            tickLine={false}
                            axisLine={false}
                            width={36}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                            tickLine={false}
                            axisLine={false}
                            width={64}
                            tickFormatter={(v: number) =>
                              v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
                            }
                          />
                          <RechartsTooltip
                            cursor={{ fill: 'hsl(var(--muted-foreground) / 0.08)' }}
                            contentStyle={{
                              background: 'hsl(var(--popover))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: 8,
                              fontSize: 12,
                            }}
                            formatter={(value, name) =>
                              name === 'revenue'
                                ? [formatCurrency(Number(value)), 'Monto']
                                : [String(value), 'Cotizaciones']
                            }
                            labelFormatter={(label) => `Mes: ${String(label)}`}
                          />
                          <Legend
                            wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
                            iconType="circle"
                            formatter={(value) => (value === 'count' ? 'Cotizaciones' : 'Monto')}
                          />
                          <Bar
                            yAxisId="left"
                            dataKey="count"
                            fill="hsl(var(--primary) / 0.55)"
                            radius={[6, 6, 0, 0]}
                            maxBarSize={42}
                          />
                          <Area
                            yAxisId="right"
                            type="monotone"
                            dataKey="revenue"
                            stroke="hsl(var(--primary))"
                            strokeWidth={2.5}
                            fill="url(#revenueGradient)"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm text-muted-foreground">Total del periodo</span>
                      <span className="text-sm font-semibold">
                        {formatCurrency(stats.quotesByMonth.reduce((s, m) => s + m.revenue, 0))}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                    <BarChart3 className="h-10 w-10 mb-2 opacity-30" />
                    <p className="text-sm">Sin datos de cotizaciones aun</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>

          {/* Status Distribution */}
          <FadeIn delay={0.35}>
            <Card data-tour="dashboard-status" className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  Distribución por estado
                </CardTitle>
                <CardDescription>Estado actual de cotizaciones</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="h-8 w-full" />
                    ))}
                  </div>
                ) : stats && stats.quotes.total > 0 ? (
                  <div className="space-y-3">
                    {/* Stacked bar */}
                    <div className="flex h-3 rounded-full overflow-hidden bg-muted">
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                        const count =
                          (stats.quotes[
                            key
                              .toLowerCase()
                              .replace(/_([a-z])/g, (_, c) =>
                                c.toUpperCase()
                              ) as keyof typeof stats.quotes
                          ] as number) || 0;
                        const pct = (count / stats.quotes.total) * 100;
                        if (pct === 0) return null;
                        return (
                          <div
                            key={key}
                            className={`${config.color} transition-all duration-700`}
                            style={{ width: `${pct}%` }}
                            title={`${config.label}: ${count}`}
                          />
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="space-y-2 mt-4">
                      {(
                        [
                          { key: 'approved', status: 'APPROVED' },
                          { key: 'pendingApproval', status: 'PENDING_APPROVAL' },
                          { key: 'draft', status: 'DRAFT' },
                          { key: 'rejected', status: 'REJECTED' },
                          { key: 'expired', status: 'EXPIRED' },
                        ] as const
                      ).map(({ key, status }) => {
                        const count = stats.quotes[key];
                        if (count === 0) return null;
                        const config = STATUS_CONFIG[status];
                        const pct = Math.round((count / stats.quotes.total) * 100);
                        return (
                          <div key={status} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${config.color}`} />
                              <span className="text-sm">{config.label}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold">{count}</span>
                              <span className="text-xs text-muted-foreground w-8 text-right">
                                {pct}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <FileText className="h-10 w-10 mb-2 opacity-30" />
                    <p className="text-sm">Sin cotizaciones aun</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>
        </div>

        {/* Analytics Row: Forecast + Salud + Top Clientes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Pipeline Forecast */}
          <FadeIn delay={0.38}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="h-4 w-4 text-violet-500" />
                  Pronóstico del pipeline
                </CardTitle>
                <CardDescription>Proyección basada en tu tasa de aprobación</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-24 w-full" />
                ) : stats ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Monto proyectado</p>
                      <p className="text-2xl font-bold text-violet-600 mt-1">
                        {formatCurrency(stats.forecast.forecastedRevenue)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground">Pipeline abierto</p>
                        <p className="text-sm font-semibold">
                          {formatCurrency(stats.forecast.totalPending)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Tasa de cierre</p>
                        <p className="text-sm font-semibold">
                          {stats.forecast.winRate.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </FadeIn>

          {/* Salud del Pipeline */}
          <FadeIn delay={0.42}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-4 w-4 text-ok" />
                  Salud del pipeline
                </CardTitle>
                <CardDescription>Tiempo de cierre y tendencia de la tasa de cierre</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-24 w-full" />
                ) : stats ? (
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Tiempo de cierre</p>
                        <p className="text-2xl font-bold text-ok mt-1">
                          {stats.closeMetrics.avgClosingDays > 0
                            ? `${stats.closeMetrics.avgClosingDays}d`
                            : '—'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Tasa de cierre actual</p>
                        <p className="text-sm font-semibold">
                          {stats.forecast.winRate.toFixed(0)}%
                        </p>
                      </div>
                    </div>
                    <div className="h-14 -mx-2">
                      {stats.closeMetrics.winRateByMonth.length > 1 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={stats.closeMetrics.winRateByMonth.map((m) => ({
                              ...m,
                              label: formatMonthLabel(m.month),
                            }))}
                            margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                          >
                            <RechartsTooltip
                              cursor={{ stroke: 'hsl(var(--muted-foreground) / 0.2)' }}
                              contentStyle={{
                                background: 'hsl(var(--popover))',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: 6,
                                fontSize: 11,
                                padding: '4px 8px',
                              }}
                              formatter={(v) => [
                                formatoPorcentaje(Number(v), { decimales: 0 }),
                                'Tasa de cierre',
                              ]}
                              labelFormatter={(l) => `Mes: ${String(l)}`}
                            />
                            <XAxis dataKey="label" hide />
                            <YAxis hide domain={[0, 100]} />
                            <Line
                              type="monotone"
                              dataKey="winRate"
                              stroke="hsl(var(--primary))"
                              strokeWidth={2}
                              dot={{ r: 2.5, fill: 'hsl(var(--primary))' }}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-xs text-muted-foreground text-center pt-3">
                          Sin suficiente historial
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </FadeIn>

          {/* Top Clientes */}
          <FadeIn delay={0.46} className="md:col-span-2 lg:col-span-1">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-info" />
                  Los 5 clientes principales
                </CardTitle>
                <CardDescription>Por monto aprobado</CardDescription>
              </CardHeader>
              <CardContent className="pb-2">
                {loading ? (
                  <Skeleton className="h-32 w-full" />
                ) : stats && stats.topClients.length > 0 ? (
                  <div className="h-44 -ml-3">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={stats.topClients.map((c) => ({
                          ...c,
                          shortName: c.name.length > 22 ? c.name.slice(0, 20) + '…' : c.name,
                        }))}
                        layout="vertical"
                        margin={{ top: 4, right: 12, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                          stroke="hsl(var(--muted-foreground) / 0.15)"
                        />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v: number) =>
                            v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
                          }
                        />
                        <YAxis
                          type="category"
                          dataKey="shortName"
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          tickLine={false}
                          axisLine={false}
                          width={120}
                        />
                        <RechartsTooltip
                          cursor={{ fill: 'hsl(var(--muted-foreground) / 0.08)' }}
                          contentStyle={{
                            background: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          formatter={(value, _name, item) => [
                            `${formatCurrency(Number(value))} · ${item?.payload?.count} cotizaciones`,
                            'Monto aprobado',
                          ]}
                          labelFormatter={(_l, payload) => payload?.[0]?.payload?.name || ''}
                        />
                        <Bar
                          dataKey="revenue"
                          fill="hsl(var(--primary))"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={20}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <Users className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-xs">Aún no hay cotizaciones aprobadas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>
        </div>

        {/* Analytics Row 2: Margen + Top Modelos + Funnel */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Margen real + distribucion */}
          <FadeIn delay={0.5}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Percent className="h-4 w-4 text-info" />
                  Margen real
                </CardTitle>
                <CardDescription>Promedio y distribución de aprobadas</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-32 w-full" />
                ) : stats && stats.marginMetrics.sampleSize > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Margen promedio</p>
                        <p
                          className={`text-3xl font-bold mt-1 ${stats.marginMetrics.avgMargin >= 20 ? 'text-ok' : stats.marginMetrics.avgMargin >= 10 ? 'text-alerta' : 'text-bloqueo'}`}
                        >
                          {stats.marginMetrics.avgMargin.toFixed(1)}%
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        n={stats.marginMetrics.sampleSize}
                      </span>
                    </div>
                    <div className="h-24 -mx-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={stats.marginMetrics.buckets}
                          margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="hsl(var(--muted-foreground) / 0.12)"
                          />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis hide />
                          <RechartsTooltip
                            cursor={{ fill: 'hsl(var(--muted-foreground) / 0.08)' }}
                            contentStyle={{
                              background: 'hsl(var(--popover))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: 6,
                              fontSize: 11,
                              padding: '4px 8px',
                            }}
                            formatter={(v) => [`${v} cotizaciones`, 'Margen']}
                          />
                          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                            {stats.marginMetrics.buckets.map((_, i) => {
                              const colors = [
                                '#ef4444',
                                '#f59e0b',
                                '#fbbf24',
                                '#10b981',
                                '#059669',
                              ];
                              return <Cell key={i} fill={colors[i]} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <Percent className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-xs">Sin cotizaciones aprobadas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>

          {/* Top modelos de pricing */}
          <FadeIn delay={0.54}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Layers className="h-4 w-4 text-info" />
                  Top Modelos
                </CardTitle>
                <CardDescription>Modelos de pricing que más vendieron</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-44 w-full" />
                ) : stats && stats.topModels.length > 0 ? (
                  <div className="h-44 -ml-3">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={stats.topModels.map((m) => ({
                          ...m,
                          shortName: m.name.length > 22 ? m.name.slice(0, 20) + '…' : m.name,
                        }))}
                        layout="vertical"
                        margin={{ top: 4, right: 12, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                          stroke="hsl(var(--muted-foreground) / 0.15)"
                        />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v: number) =>
                            v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
                          }
                        />
                        <YAxis
                          type="category"
                          dataKey="shortName"
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          tickLine={false}
                          axisLine={false}
                          width={110}
                        />
                        <RechartsTooltip
                          cursor={{ fill: 'hsl(var(--muted-foreground) / 0.08)' }}
                          contentStyle={{
                            background: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          formatter={(value, _name, item) => [
                            `${formatCurrency(Number(value))} · ${item?.payload?.count} cotizaciones`,
                            'Monto aprobado',
                          ]}
                          labelFormatter={(_l, payload) => payload?.[0]?.payload?.name || ''}
                        />
                        <Bar
                          dataKey="revenue"
                          fill="hsl(var(--primary))"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={20}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <Layers className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-xs">Aún no hay modelos usados</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>

          {/* Funnel de conversion */}
          <FadeIn delay={0.58}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Filter className="h-4 w-4 text-purple-500" />
                  Funnel de conversión
                </CardTitle>
                <CardDescription>De borrador a aprobada</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-44 w-full" />
                ) : stats && stats.quotes.total > 0 ? (
                  (() => {
                    const stages = [
                      {
                        label: 'Creadas',
                        value: stats.quotes.total,
                        color: 'bg-info',
                        text: 'text-primary',
                      },
                      {
                        label: 'Enviadas',
                        value:
                          stats.quotes.pendingApproval +
                          stats.quotes.approved +
                          stats.quotes.rejected +
                          stats.quotes.expired,
                        color: 'bg-alerta',
                        text: 'text-alerta',
                      },
                      {
                        label: 'Aprobadas',
                        value: stats.quotes.approved,
                        color: 'bg-ok',
                        text: 'text-ok',
                      },
                    ];
                    const max = stages[0].value || 1;
                    return (
                      <div className="space-y-3">
                        {stages.map((s, i) => {
                          const pct = (s.value / max) * 100;
                          const prevPct =
                            i > 0 && stages[i - 1].value > 0
                              ? Math.round((s.value / stages[i - 1].value) * 100)
                              : null;
                          return (
                            <div key={s.label}>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-medium">{s.label}</span>
                                <span className={`font-semibold ${s.text}`}>
                                  {s.value}
                                  {prevPct !== null && (
                                    <span className="text-muted-foreground font-normal ml-2">
                                      ({prevPct}% del paso anterior)
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className="h-3 rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full ${s.color} transition-all duration-700`}
                                  style={{ width: `${Math.max(pct, 2)}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between pt-2 border-t text-xs">
                          <span className="text-muted-foreground">Conversión total</span>
                          <span className="font-semibold text-ok">
                            {Math.round((stats.quotes.approved / stats.quotes.total) * 100)}%
                          </span>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <Filter className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-xs">Sin cotizaciones</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>
        </div>

        {/* Analytics Row 3: Tamano de deal + Descuento */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Deal size distribution */}
          <FadeIn delay={0.6} className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="h-4 w-4 text-info" />
                  Tamano de deal
                </CardTitle>
                <CardDescription>Distribución de cotizaciones aprobadas por monto</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-40 w-full" />
                ) : stats && stats.dealSizeDistribution.some((b) => b.count > 0) ? (
                  <div className="h-44 -mx-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={stats.dealSizeDistribution}
                        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="hsl(var(--muted-foreground) / 0.15)"
                        />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          tickLine={false}
                          axisLine={{ stroke: 'hsl(var(--muted-foreground) / 0.2)' }}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          tickLine={false}
                          axisLine={false}
                          width={32}
                          allowDecimals={false}
                        />
                        <RechartsTooltip
                          cursor={{ fill: 'hsl(var(--muted-foreground) / 0.08)' }}
                          contentStyle={{
                            background: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          formatter={(_v, _n, item) => [
                            `${item?.payload?.count} cotizaciones · ${formatCurrency(item?.payload?.revenue || 0)}`,
                            'Total',
                          ]}
                        />
                        <Bar
                          dataKey="count"
                          fill="hsl(var(--primary))"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={48}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <BarChart3 className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-xs">Sin cotizaciones aprobadas</p>
                  </div>
                )}
                {!loading && stats && stats.discountMetrics.largestDeal > 0 && (
                  <div className="flex items-center justify-between pt-3 mt-2 border-t text-xs">
                    <span className="text-muted-foreground">Deal más grande</span>
                    <span className="font-semibold">
                      {formatCurrency(stats.discountMetrics.largestDeal)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>

          {/* Discount metrics */}
          <FadeIn delay={0.64}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Tag className="h-4 w-4 text-bloqueo" />
                  Descuento aplicado
                </CardTitle>
                <CardDescription>Sobre cotizaciones aprobadas</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-32 w-full" />
                ) : stats && stats.discountMetrics.approvedCount > 0 ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground">% con descuento</p>
                      <p
                        className={`text-3xl font-bold mt-1 ${stats.discountMetrics.discountedPct >= 50 ? 'text-bloqueo' : stats.discountMetrics.discountedPct >= 25 ? 'text-alerta' : 'text-ok'}`}
                      >
                        {stats.discountMetrics.discountedPct.toFixed(0)}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {stats.discountMetrics.discountedCount} de{' '}
                        {stats.discountMetrics.approvedCount} aprobadas
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground">Descuento promedio</p>
                        <p className="text-sm font-semibold">
                          {stats.discountMetrics.avgDiscountPct.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total descontado</p>
                        <p className="text-sm font-semibold">
                          {formatCurrency(stats.discountMetrics.totalDiscountAmount)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <Tag className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-xs">Sin cotizaciones aprobadas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>
        </div>

        {/* Bottom Row: Recent Quotes + Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Quotes Table */}
          <FadeIn delay={0.4} className="lg:col-span-2">
            <Card data-tour="dashboard-recent">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Cotizaciones Recientes</CardTitle>
                    <CardDescription>Últimas 5 cotizaciones creadas</CardDescription>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/quotes')}>
                        Ver todas
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Ir al listado completo de cotizaciones con filtros y búsqueda
                    </TooltipContent>
                  </Tooltip>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : stats && stats.recentQuotes.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Monto</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats.recentQuotes.map((q) => {
                        const statusCfg = STATUS_CONFIG[q.status] || STATUS_CONFIG.DRAFT;
                        return (
                          <TableRow
                            key={q.id}
                            className="cursor-pointer"
                            onClick={() => navigate(`/quotes/${q.id}`)}
                          >
                            <TableCell className="font-mono text-sm font-medium">
                              {q.code}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{q.clientName}</p>
                                <p className="text-xs text-muted-foreground">{q.createdBy}</p>
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold">
                              {formatCurrency(q.finalPrice, q.currencyCode)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                            </TableCell>
                            <TableCell className="text-right text-sm text-muted-foreground">
                              {formatRelativeTime(q.createdAt)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <FileText className="h-10 w-10 mb-3 opacity-30" />
                    <p className="text-sm font-medium">No hay cotizaciones aun</p>
                    {/*
                      La invitación se va junto con el botón: prometer «crea tu
                      primera cotizacion» a quien no tiene el módulo es la misma
                      confusión, sólo sin nada que apretar.
                    */}
                    {nuevaCotizacion && (
                      <>
                        <p className="text-xs mt-1">Crea tu primera cotización para empezar</p>
                        <Button
                          onClick={() => navigate(nuevaCotizacion.href)}
                          className="mt-4"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Crear Cotización
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </FadeIn>

          {/* Sidebar: Exchange rate + user info */}
          <FadeIn delay={0.45}>
            <div className="space-y-4">
              <ExchangeRateWidget />
              {/* L5 (vault 278): las alertas de Precios Dinámicos de esta persona. */}
              {canSeeMenu('pricing') && !isModuleLocked('pricing') && <MarketAlertsWidget />}

              <Card className="bg-muted/30">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                      {fullName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{fullName}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <Badge variant={isAdmin ? 'destructive' : 'default'} className="shrink-0">
                      {user?.role?.replace('_', ' ')}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </FadeIn>
        </div>

        {/* Expiring Soon */}
        <FadeIn delay={0.48}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-alerta" />
                    Cotizaciones por vencer
                  </CardTitle>
                  <CardDescription>
                    Vencen en los proximos 7 días y todavia estan abiertas
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : stats && stats.expiringSoonQuotes.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Vence en</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.expiringSoonQuotes.map((q) => {
                      const statusCfg = STATUS_CONFIG[q.status] || STATUS_CONFIG.DRAFT;
                      const urgent = q.daysLeft <= 2;
                      return (
                        <TableRow
                          key={q.id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/quotes/${q.id}`)}
                        >
                          <TableCell className="font-mono text-sm font-medium">{q.code}</TableCell>
                          <TableCell>
                            <p className="font-medium text-sm">{q.clientName}</p>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(q.finalPrice, q.currencyCode)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span
                              className={`text-sm font-medium ${urgent ? 'text-bloqueo' : 'text-alerta'}`}
                            >
                              {q.daysLeft === 0 ? 'hoy' : `${q.daysLeft}d`}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                  <CheckCircle className="h-10 w-10 mb-3 text-ok opacity-60" />
                  <p className="text-sm font-medium">Sin cotizaciones por vencer</p>
                  <p className="text-xs mt-1">Ninguna vence en los proximos 7 días</p>
                </div>
              )}
            </CardContent>
          </Card>
        </FadeIn>

        {/* Not Approved Quotes */}
        <FadeIn delay={0.5}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-alerta" />
                    Cotizaciones no aprobadas
                  </CardTitle>
                  <CardDescription>Pendientes, rechazadas, expiradas o en borrador</CardDescription>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/quotes?status=PENDING_APPROVAL')}
                    >
                      Ver todas
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Ir al listado de cotizaciones filtrado</TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : stats && stats.notApprovedQuotes.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.notApprovedQuotes.map((q) => {
                      const statusCfg = STATUS_CONFIG[q.status] || STATUS_CONFIG.DRAFT;
                      return (
                        <TableRow
                          key={q.id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/quotes/${q.id}`)}
                        >
                          <TableCell className="font-mono text-sm font-medium">{q.code}</TableCell>
                          <TableCell>
                            <p className="font-medium text-sm">{q.clientName}</p>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(q.finalPrice, q.currencyCode)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">
                            {formatRelativeTime(q.createdAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                  <CheckCircle className="h-10 w-10 mb-3 text-ok opacity-60" />
                  <p className="text-sm font-medium">No tenes cotizaciones pendientes</p>
                  <p className="text-xs mt-1">Todas tus cotizaciones estan aprobadas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </FadeIn>
      </div>
    </AppLayout>
  );
}

export default DashboardPage;
